import { useState, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button } from '@/shared/components/ui/button';
import { personLabelFor, pruneHiddenAnswers } from '../api/v2TakeApi';
import type { DiscoveryAnswers, V2Response, V2TakeContent } from '../api/v2TakeApi';
import { LandingScreen } from '../components/flowScreens';
import { V2_LANDING } from '../components/v2Landing';
import { V2DiscoveryScreen } from '../components/V2DiscoveryScreen';
import { V2PillarScreen } from '../components/V2PillarScreen';
import {
  clearDraft,
  loadDraft,
  saveDraft,
  type DraftFields,
  type DraftStep,
} from './simulatorDraft';

/**
 * The V2 take flow for the public simulator: landing → discovery → five pillars
 * → finished, over a localStorage draft instead of the token API.
 *
 * A separate container rather than a port in `V2TakeFlow`, which stays as it
 * is: the token side differs at saving, seeding, landing and completion. The
 * cost is this duplicated step machine — a fix to advancing or going back
 * belongs in both. Every change is saved synchronously, so there is no saving
 * state and nothing to fail; the screens' `isSaving`/`saveError` stay unset.
 */

/** Same names the public report endpoint puts on both reports. */
const CLIENT_NAME = 'Your client';
const ADVISOR_NAME = 'You';

type Step = 'landing' | DraftStep;

type FinishedProps = {
  draft: DraftFields;
  startOver: () => void;
  /** Saves how the email gate was resolved; the finished view re-renders with it. */
  setGate: (gate: 'email' | 'skipped') => void;
  /** Start over because the answers no longer fit: the landing says it expired. */
  expire: () => void;
};

type SimulatorTakeFlowProps = {
  content: V2TakeContent;
  /** What follows the last pillar — the email gate and the reports. */
  renderFinished: (props: FinishedProps) => ReactNode;
};

const EMPTY: Required<Pick<DraftFields, 'gate' | 'discovery' | 'answers' | 'subjectName'>> = {
  gate: null,
  discovery: {},
  answers: {},
  subjectName: '',
};

/** The saved draft, minus one whose step this content has no pillar for. */
const loadFitting = (content: V2TakeContent) => {
  const loaded = loadDraft(content.version);
  const step = loaded.draft?.step;
  if (typeof step === 'number' && step >= content.pillars.length) {
    clearDraft();
    return { draft: null, discarded: true };
  }
  return loaded;
};

export function SimulatorTakeFlow({ content, renderFinished }: SimulatorTakeFlowProps) {
  const reduceMotion = useReducedMotion();
  const [initial] = useState(() => loadFitting(content));
  const [discarded, setDiscarded] = useState(initial.discarded);
  // The step a Continue on the landing goes back to; null = nothing saved.
  const [savedStep, setSavedStep] = useState<DraftStep | null>(initial.draft?.step ?? null);
  const [step, setStep] = useState<Step>(initial.draft?.step === 'report' ? 'report' : 'landing');
  const [fields, setFields] = useState<typeof EMPTY>(() =>
    initial.draft
      ? {
          gate: initial.draft.gate,
          discovery: initial.draft.discovery,
          answers: initial.draft.answers,
          subjectName: initial.draft.subjectName ?? '',
        }
      : EMPTY
  );

  const pillars = content.pillars;
  const { personLabels } = content;
  // The name only exists while D1 says the visitor answers about someone else.
  const isSupporter = personLabelFor(fields.discovery, personLabels) !== undefined;

  const commit = (nextStep: DraftStep, patch: Partial<typeof fields> = {}) => {
    const next = { ...fields, ...patch };
    saveDraft({ contentVersion: content.version, step: nextStep, ...next });
    setFields(next);
    setStep(nextStep);
    setSavedStep(nextStep);
  };

  const startOver = () => {
    clearDraft();
    setFields(EMPTY);
    setSavedStep(null);
    setDiscarded(false);
  };

  const onDiscoveryAnswer = (questionId: string, optionKeys: string[]) => {
    const discovery: DiscoveryAnswers = pruneHiddenAnswers(
      { ...fields.discovery, [questionId]: optionKeys },
      content.discoveryQuestions,
      content.escalationChecks,
      content.escalationShowWhen
    );
    // Moving to "Me" clears the name; parent → spouse keeps it.
    const leftSupporter =
      questionId === 'D1' && personLabelFor({ D1: optionKeys }, personLabels) === undefined;
    commit('discovery', leftSupporter ? { discovery, subjectName: '' } : { discovery });
  };

  const onPillarAnswer = (index: number, needId: string, response: V2Response) =>
    commit(index, { answers: { ...fields.answers, [needId]: response } });

  const node = (): ReactNode => {
    if (step === 'landing') {
      return (
        <>
          {discarded && (
            <p className="mx-auto mb-4 max-w-xl text-center text-sm text-gray-600" role="status">
              Your previous simulation expired — start a new one.
            </p>
          )}
          {savedStep === null ? (
            <LandingScreen
              clientName={CLIENT_NAME}
              advisorName={ADVISOR_NAME}
              onStart={() => commit('discovery')}
              {...V2_LANDING}
            />
          ) : (
            <div className="mx-auto w-full max-w-xl rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
              <h1 className="font-serif text-2xl text-gray-900">Pick up where you left off?</h1>
              <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-gray-600">
                Your answers from last time are saved in this browser.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    startOver();
                    commit('discovery', EMPTY);
                  }}
                >
                  Start over
                </Button>
                <Button
                  type="button"
                  className="bg-[#026857] hover:bg-[#025247]"
                  onClick={() => setStep(savedStep)}
                >
                  Continue
                </Button>
              </div>
            </div>
          )}
        </>
      );
    }
    if (step === 'discovery') {
      return (
        <V2DiscoveryScreen
          questions={content.discoveryQuestions}
          escalationChecks={content.escalationChecks}
          escalationShowWhen={content.escalationShowWhen}
          answers={fields.discovery}
          onAnswer={onDiscoveryAnswer}
          subjectName={isSupporter ? fields.subjectName : undefined}
          onSubjectNameChange={(subjectName) => commit('discovery', { subjectName })}
          onBack={() => setStep('landing')}
          onContinue={() => commit(0)}
        />
      );
    }
    if (typeof step === 'number') {
      return (
        <V2PillarScreen
          pillar={pillars[step]}
          index={step}
          total={pillars.length}
          responseOptions={content.responseOptions}
          answers={fields.answers}
          discovery={fields.discovery}
          personLabels={personLabels}
          personOwnLabels={content.personOwnLabels}
          subjectName={fields.subjectName}
          onAnswer={(needId, response) => onPillarAnswer(step, needId, response)}
          onBack={() => commit(step === 0 ? 'discovery' : step - 1)}
          onContinue={() => commit(step === pillars.length - 1 ? 'report' : step + 1)}
        />
      );
    }
    return renderFinished({
      draft: { contentVersion: content.version, step: 'report', ...fields },
      startOver: () => {
        startOver();
        setStep('landing');
      },
      setGate: (gate) => commit('report', { gate }),
      expire: () => {
        startOver();
        setDiscarded(true);
        setStep('landing');
      },
    });
  };

  // Same transition as the token flow's page: fade + gentle rise, fade only
  // under prefers-reduced-motion.
  const variants = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: 12 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -12 },
      };

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={String(step)}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.15, ease: 'easeOut' }}
      >
        {node()}
      </motion.div>
    </AnimatePresence>
  );
}
